import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  IosShare as ExportIcon,
  ContentCopy as CopyIcon,
  SmartToy as PromptIcon,
  FileDownload as DownloadIcon,
  DataObject as JsonIcon,
} from '@mui/icons-material';
import { personaService } from '../services/api';
import type { Persona } from '@personawiz/shared';
import PersonaCard from '../components/persona/PersonaCard';
import {
  personaToMarkdown,
  personaToSystemPrompt,
  copyToClipboard,
  downloadPersonaJSON,
} from '../utils/personaExport';

export default function PersonaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [persona, setPersona] = useState<Persona | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    if (!id) return;

    personaService.getById(id)
      .then((data) => setPersona(data))
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load persona.');
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this persona?')) return;

    setIsDeleting(true);
    try {
      await personaService.delete(id);
      navigate('/personas');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'Failed to delete persona.');
      setIsDeleting(false);
    }
  };

  const handleExport = async (type: 'markdown' | 'system-prompt' | 'json-copy' | 'json-download') => {
    if (!persona) return;
    setExportAnchor(null);

    switch (type) {
      case 'markdown': {
        const ok = await copyToClipboard(personaToMarkdown(persona));
        setSnackbar(ok ? 'Markdown summary copied to clipboard!' : 'Failed to copy');
        break;
      }
      case 'system-prompt': {
        const ok = await copyToClipboard(personaToSystemPrompt(persona));
        setSnackbar(ok ? 'System prompt copied to clipboard!' : 'Failed to copy');
        break;
      }
      case 'json-copy': {
        const ok = await copyToClipboard(JSON.stringify(persona, null, 2));
        setSnackbar(ok ? 'JSON copied to clipboard!' : 'Failed to copy');
        break;
      }
      case 'json-download':
        downloadPersonaJSON(persona);
        setSnackbar('JSON file downloaded!');
        break;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !persona) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Persona not found.'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/personas')}>
          Back to Personas
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          underline="hover"
          color="inherit"
        >
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/personas')}
          underline="hover"
          color="inherit"
        >
          Personas
        </Link>
        <Typography variant="body2" color="text.primary">
          {persona.coreIdentity.personaName}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/personas')}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight={700}>
            {persona.coreIdentity.personaName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<ExportIcon />}
            variant="contained"
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => handleExport('markdown')}>
              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Copy as Markdown</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('system-prompt')}>
              <ListItemIcon><PromptIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Copy as System Prompt</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('json-copy')}>
              <ListItemIcon><JsonIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Copy as JSON</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleExport('json-download')}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Download JSON File</ListItemText>
            </MenuItem>
          </Menu>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Box>

      {/* Persona Card */}
      <PersonaCard persona={persona} expanded />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={3000}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </Box>
  );
}
